import { BaseWidgetRenderer } from './base-widget-renderer';
import { WidgetJsonData } from '../../models/element-types';
import { HtmlUtils } from '../../utils/html.utils';

/**
 * The managed-button row's DOM contract — the shared half of the submit and
 * nav-button renderers: the .button-element builder (variant class + sanitized
 * label span) and the wrapper's button-group attributes. The class names and the
 * 'buttons' group marker are the runtime's navigation hooks (Web.Static's Form
 * binds .button-element--previous/--next) and the server's submit-row scaffold
 * mirrors them — one place on each side, pinned by WidgetSchemaParityTest.
 */
export abstract class BaseButtonWidgetRenderer<TWidgetJsonData extends WidgetJsonData> extends BaseWidgetRenderer<TWidgetJsonData> {
    /** The default row height of a managed button — server parity
     *  (FormPageContentService.DefaultButtonHeight). */
    protected static readonly defaultButtonHeight = '40px';

    /** One managed button: .button-element--{variant} + a sanitized label span. */
    protected createButton(variant: string, label: string, type: 'button' | 'submit' = 'button'): HTMLElement {
        const button = document.createElement('button');
        button.classList.add('button-element', `button-element--${variant}`);
        button.type = type;

        const labelSpan = document.createElement('span');
        labelSpan.classList.add('button-label');
        labelSpan.innerHTML = HtmlUtils.sanitize(label);

        button.appendChild(labelSpan);

        return button;
    }

    /** The wrapper attributes shared by every managed-button widget: the 'buttons'
     *  element group and the row height. */
    protected applyButtonElementAttributes(element: HTMLElement, height: string): void {
        element.dataset['group'] = 'buttons';
        element.style.height = height;
    }
}
